import { useState } from "react";
import { BookOpenCheck, ArrowUpRight } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import { CarouselModal } from "../shared/CreativeCarousel";
import { resolveIcon } from "../shared/iconMap";
import Dec26Modal from "../shared/Dec26Modal";
import { AirlineSlideHead } from "../shared/AirlineSlideRenderers";

const SlideSQCreativity = () => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const brand = airline.brandColor || "#F2A900";
  const c = airline?.content?.creativity || {};
  const clusters = c.clusters || [];
  const showDec26Cta = c.showDec26;

  const [activeClusterId, setActiveClusterId] = useState(null);
  const [showDec26, setShowDec26] = useState(false);

  const resolveColor = (col) => (col === "brand" ? brand : col || brand);
  const active = clusters.find((cl) => cl.id === activeClusterId);

  return (
    <div>
      <AirlineSlideHead
        eyebrow="Creativity"
        title="Programming vision,"
        accentLine="from studio to seat."
        airline={airline}
        accent={brand}
      />
      <p className={`k-lead max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
        {c.subheading}
      </p>

      <div className={`mt-12 pt-8 border-t grid grid-cols-3 gap-6 ${isDark ? "border-white/10" : "border-black/10"}`}>
        {clusters.map((cluster) => {
          const Icon = resolveIcon(cluster.iconName);
          const color = resolveColor(cluster.color);
          return (
            <button
              type="button"
              key={cluster.id}
              onClick={() => setActiveClusterId(cluster.id)}
              data-testid={`sq-cluster-${cluster.id}`}
              className={`text-left rounded-2xl p-6 border transition-all hover:-translate-y-0.5 ${
                isDark ? "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]" : "bg-white border-black/10 hover:border-black/25"
              }`}
            >
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${color}22` }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <div className={`text-[22px] font-semibold tracking-tight leading-tight mb-3 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
                {cluster.title}
              </div>
              <div className="flex flex-wrap gap-1.5 mb-6">
                {cluster.tags.slice(0, 6).map((tag) => (
                  <span key={tag} className="text-[12px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>{tag}</span>
                ))}
              </div>
              <div className="flex items-center gap-1 text-[13px] font-semibold" style={{ color }}>
                Explore <ArrowUpRight className="w-3.5 h-3.5" />
              </div>
            </button>
          );
        })}
      </div>

      {showDec26Cta && (
        <button
          type="button"
          onClick={() => setShowDec26(true)}
          data-testid="open-dec26-cta"
          className={`mt-8 w-full flex items-center justify-between gap-4 rounded-2xl px-6 py-5 border transition-all hover:-translate-y-0.5 ${
            isDark
              ? "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]"
              : "bg-white border-black/10 hover:border-black/25"
          }`}
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brand}25` }}>
              <BookOpenCheck className="w-5 h-5" style={{ color: brand }} />
            </div>
            <div className="text-left">
              <div className={`text-[17px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{c.dec26Label}</div>
              <div className={`text-[13px] mt-0.5 ${isDark ? "text-white/55" : "text-black/55"}`}>{c.dec26Sub}</div>
            </div>
          </div>
          <ArrowUpRight className="w-5 h-5 flex-shrink-0" style={{ color: brand }} />
        </button>
      )}

      {active && (
        <CarouselModal
          items={active.items}
          title={`${active.title} — ${airline.name}`}
          accentColor={resolveColor(active.color)}
          onClose={() => setActiveClusterId(null)}
        />
      )}
      {showDec26 && <Dec26Modal isDark={isDark} onClose={() => setShowDec26(false)} />}
    </div>
  );
};

export default SlideSQCreativity;
